import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button/Button";
import { useDashboard } from "../../context/DashboardContext";
import type { DashboardConfig } from "../../types/dashboard";
import { createWidgetConfig } from "../../services/api/widget";
import { addWidget as addDashboardWidget } from "../../services/api/dashboard";
import { useNotification } from "../../context/NotificationContext";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../ui/dialog/Dialog";
import Select from "../ui/select-dropdown";
import FormInput from "../ui/form-input";

interface AddWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  widgetType: "playground" | "screener" | "contributions";
  initialConfig?: any;
}

export function AddWidgetModal({
  isOpen,
  onClose,
  widgetType,
  initialConfig,
}: AddWidgetModalProps) {
  const [title, setTitle] = useState("");
  const [selectedDashboard, setSelectedDashboard] =
    useState<DashboardConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { dashboards, refreshDashboards, getNextRowPosition } = useDashboard();
  const notification = useNotification();
  
  // Track previous isOpen state to detect when modal opens
  const wasOpen = useRef(false);
  // Track the selected dashboard ID to avoid stale reference issues
  const selectedDashboardId = useRef<string | null>(null);

  // Extract to ensure consistent dependency array size
  const activeWatchlistName = initialConfig?.activeWatchlistName ?? "";

  // Reset form state when modal opens or closes
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      // Modal just opened - reset form
      refreshDashboards();
      setIsLoading(false);
      setTitle(activeWatchlistName);
      // Reset dashboard selection - will be set by next effect
      selectedDashboardId.current = null;
      setSelectedDashboard(null);
    } else if (!isOpen && wasOpen.current) {
      // Modal just closed - reset to loading state for clean next open
      setIsLoading(true);
    }
    wasOpen.current = isOpen;
  }, [isOpen, refreshDashboards, activeWatchlistName]);

  // Set default dashboard selection when dashboards load
  useEffect(() => {
    if (isOpen && Array.isArray(dashboards) && dashboards.length > 0) {
      // Only update if we don't have a valid selection yet
      if (!selectedDashboardId.current) {
        setSelectedDashboard(dashboards[0]);
        selectedDashboardId.current = dashboards[0].id;
      } else {
        // Check if our saved ID still exists in the list
        const existingDashboard = dashboards.find(d => d.id === selectedDashboardId.current);
        if (existingDashboard) {
          setSelectedDashboard(existingDashboard);
        } else {
          // Saved selection no longer exists, select first
          setSelectedDashboard(dashboards[0]);
          selectedDashboardId.current = dashboards[0].id;
        }
      }
    }
  }, [isOpen, dashboards]);

  const handleSubmit = async () => {
    if (!selectedDashboard?.id) {
      alert("Please select a dashboard");
      return;
    }

    try {
      // First create the widget config
      const widgetConfigResponse = await createWidgetConfig({
        type: widgetType,
        name: title,
        config: {
          ...initialConfig,
          // For screener widgets, ensure viewId is included
          ...(widgetType === "screener" && {
            viewId: initialConfig?.viewId,
          }),
          // Ensure exposure_type is included if it exists in initialConfig
          ...(initialConfig?.exposureType && {
            exposure_type: initialConfig.exposureType,
          }),
        },
      });

      // Get the next available row from the context - always up to date
      const nextRow = getNextRowPosition(selectedDashboard.id);

      // Set default height based on widget type - charts need more vertical space for legend
      const defaultHeight = widgetType === "playground" ? 8 : 4;

      // Then add the widget to the dashboard
      const widgetRequest = {
        config_id: widgetConfigResponse.config_id,
        type: widgetType,
        title,
        position: { col: 0, row: nextRow, width: 12, height: defaultHeight },
      };

      const response = await addDashboardWidget(
        selectedDashboard.id,
        widgetRequest
      );

      // Show success notification
      notification.showSuccess(
        "Widget Added",
        `"${title}" has been added to "${selectedDashboard.name}"`,
        {
          duration: 5000,
        }
      );

      onClose();
    } catch (error) {
      console.error("Error adding widget:", error);
      notification.showError(
        "Failed to Add Widget",
        "There was an error adding the widget to the dashboard",
        {
          duration: 8000,
        }
      );
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
    >
      <DialogTitle>Add widget</DialogTitle>
      <DialogBody>
        <div className="grid gap-4">
          <div className="grid gap-2">
            {Array.isArray(dashboards) && dashboards.length > 0 ? (
              <Select
                value={selectedDashboard?.id}
                onChange={(id) => {
                  const dashboard = dashboards.find((d) => d.id === id);
                  if (dashboard) {
                    setSelectedDashboard(dashboard);
                    selectedDashboardId.current = dashboard.id;
                  }
                }}
                options={dashboards.map((d) => ({
                  value: d.id,
                  label: d.name,
                }))}
                label="Select a dashboard"
                usePortal
              />
            ) : (
              <div className="text-sm text-gray-500">
                No dashboards available. Please create a dashboard first.
              </div>
            )}
          </div>
          <div className="grid gap-2">
            <FormInput
              id="widget-title"
              label="Title"
              type="text"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
              placeholder="Add a widget title"
            />
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose} plain>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedDashboard?.id || !title}
          color="indigo"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
